import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MascotLoader from '../components/MascotLoader';
import { MASCOT_URL } from '../assets/mascot';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Plus, FileText, Clock, CheckCircle, LogOut, Search, MapPin, Calendar, Menu, Briefcase, Mail, PenTool, Edit3, Trash2, CloudOff, Cloud, AlertTriangle, Bell, Euro, AlertCircle, Moon, Sun, Video } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';
const LOCAL_DRAFT_KEY = 'kardif_intervention_draft';

// Calcul de l'âge d'un brouillon en heures
const getDraftAge = (updatedAt) => {
  const now = new Date();
  const updated = new Date(updatedAt);
  const diffMs = now - updated;
  return diffMs / (1000 * 60 * 60); // en heures
};

// Format relatif du temps
const formatRelativeTime = (updatedAt) => {
  const hours = getDraftAge(updatedAt);
  if (hours < 1) return 'Il y a moins d\'une heure';
  if (hours < 24) return `Il y a ${Math.floor(hours)} heure${Math.floor(hours) > 1 ? 's' : ''}`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Hier';
  if (days < 7) return `Il y a ${days} jours`;
  return `Il y a plus d'une semaine`;
};

function TechnicianDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const [interventions, setInterventions] = useState([]);
  const [missions, setMissions] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [localDraft, setLocalDraft] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reminderDismissed, setReminderDismissed] = useState(false);
  const [pendingFranchises, setPendingFranchises] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [newNotifPulse, setNewNotifPulse] = useState(false);
  const prevNotifCount = useRef(null);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.36);
      const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
      o2.connect(g2); g2.connect(ctx.destination); o2.type = 'sine'; o2.frequency.value = 1174;
      g2.gain.setValueAtTime(0.0001, ctx.currentTime + 0.18);
      g2.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.2);
      g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
      o2.start(ctx.currentTime + 0.18); o2.stop(ctx.currentTime + 0.56);
    } catch (e) { /* audio non dispo */ }
  };

  const pollCorrections = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      const corr = (res.data || []).filter(n => n.type === 'report_corrected' || n.type === 'new_mission');
      setCorrections(corr);
      if (prevNotifCount.current !== null && corr.length > prevNotifCount.current) {
        const diff = corr.length - prevNotifCount.current;
        playBeep();
        setNewNotifPulse(true);
        setTimeout(() => setNewNotifPulse(false), 8000);
        toast.info(`${diff} nouvelle(s) notification(s)`, { icon: '🔔' });
      }
      prevNotifCount.current = corr.length;
    } catch (e) { /* silencieux */ }
  };

  const dismissCorrection = async (notifId) => {
    try {
      await axios.delete(`${API}/notifications/${notifId}`);
      setCorrections(prev => {
        const next = prev.filter(n => n.id !== notifId);
        prevNotifCount.current = next.length;
        return next;
      });
    } catch (e) { toast.error('Erreur'); }
  };

  useEffect(() => {
    const id = setInterval(pollCorrections, 20000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetchData();
    checkLocalDraft();
  }, []);

  // Notification pour les brouillons anciens (> 24h)
  useEffect(() => {
    if (drafts.length > 0 && !reminderDismissed) {
      const oldDrafts = drafts.filter(d => getDraftAge(d.updated_at) > 24);
      if (oldDrafts.length > 0) {
        toast.warning(
          `${oldDrafts.length} brouillon${oldDrafts.length > 1 ? 's' : ''} en attente depuis plus de 24h`,
          {
            duration: 8000,
            action: {
              label: 'Voir',
              onClick: () => document.getElementById('drafts-section')?.scrollIntoView({ behavior: 'smooth' })
            }
          }
        );
      }
    }
  }, [drafts, reminderDismissed]);

  const fetchData = async () => {
    try {
      const [intRes, statsRes, missionsRes, draftsRes, franchisesRes, notifRes] = await Promise.all([
        axios.get(`${API}/interventions`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/missions`),
        axios.get(`${API}/drafts`),
        axios.get(`${API}/franchises/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications`).catch(() => ({ data: [] }))
      ]);
      setInterventions(intRes.data);
      setStats(statsRes.data);
      setMissions(missionsRes.data.filter(m => m.status === 'scheduled'));
      setDrafts(draftsRes.data || []);
      setPendingFranchises(franchisesRes.data || []);
      const corr = (notifRes.data || []).filter(n => n.type === 'report_corrected' || n.type === 'new_mission');
      setCorrections(corr);
      prevNotifCount.current = corr.length;
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const checkLocalDraft = () => {
    const local = localStorage.getItem(LOCAL_DRAFT_KEY);
    if (local) {
      try {
        setLocalDraft(JSON.parse(local));
      } catch (e) {}
    }
  };

  const deleteDraft = async (draftId) => {
    if (!confirm('Supprimer ce brouillon ?')) return;
    try {
      await axios.delete(`${API}/drafts/${draftId}`);
      setDrafts(drafts.filter(d => d.id !== draftId));
      toast.success('Brouillon supprimé');
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const deleteLocalDraft = () => {
    localStorage.removeItem(LOCAL_DRAFT_KEY);
    setLocalDraft(null);
    toast.success('Brouillon local supprimé');
  };

  // Compter les brouillons anciens (> 24h)
  const oldDraftsCount = drafts.filter(d => getDraftAge(d.updated_at) > 24).length;

  const filteredInterventions = interventions.filter(i => 
    i.adresse_complete?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.client_nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'En attente' },
      validated: { bg: 'bg-green-100', text: 'text-green-800', label: 'Validé' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeté' }
    };
    const style = styles[status] || styles.submitted;
    return <Badge className={`${style.bg} ${style.text}`}>{style.label}</Badge>;
  };

  const getUrgencyDot = (color) => {
    const colors = { green: 'bg-green-500', orange: 'bg-amber-500', red: 'bg-red-500 animate-pulse' };
    return <span className={`inline-block w-2 h-2 rounded-full ${colors[color] || colors.green}`} />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <MascotLoader text="Chargement de vos interventions..." />;

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-[#1A1A1A]' : 'bg-[#F8F9FA]'}`} data-testid="technician-dashboard">
      <div className={`mobile-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar fixed top-0 left-0 w-64 h-screen z-50 transform transition-transform md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b">
          <img src={LOGO} alt="KARDIF" className="h-12" />
        </div>
        
        <nav className="p-4 space-y-2">
          <a href="#" className="sidebar-link active">
            <FileText className="h-5 w-5" />
            <span>Mes interventions</span>
          </a>
          <a href="/planning" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/planning'); }}>
            <Calendar className="h-5 w-5" />
            <span>Planning</span>
          </a>
          {user?.role === 'admin' && (
            <a href="/admin" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/admin'); }} data-testid="tech-back-to-admin-link">
              <Briefcase className="h-5 w-5" />
              <span>Espace admin</span>
            </a>
          )}
          <a href="/arch-planner" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/arch-planner'); }}>
            <PenTool className="h-5 w-5" />
            <span>Arch-Planner</span>
          </a>
          <a href="/visio" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/visio'); }} data-testid="nav-visio">
            <Video className="h-5 w-5" />
            <span>Visio-Assistance</span>
          </a>
          <a 
            href={`https://mail.google.com/mail/u/?authuser=${user?.email || ''}`} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="sidebar-link"
          >
            <Mail className="h-5 w-5" />
            <span>Ma boîte mail</span>
          </a>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#8B7355] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-[#6B7280]">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={() => { logout(); nav('/login'); }}>
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-64">
        <header className="md:hidden flex items-center justify-between p-4 bg-white border-b">
          <button onClick={() => setSidebarOpen(true)}><Menu className="h-6 w-6" /></button>
          <img src="/kardif-eye.png" alt="KARDIF" className="h-9 w-9 object-contain" />
          <div className="w-6" />
        </header>

        <div className="p-6 md:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <img src={MASCOT_URL} alt="KARDIF" className="hidden sm:block h-14 w-auto drop-shadow mascot-live" data-testid="header-mascot" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  Mes interventions
                </h1>
                <p className="text-[#6B7280] mt-1">Bonjour, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {corrections.length > 0 && (
                <div className="relative">
                  {newNotifPulse && (
                    <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5" data-testid="tech-notif-live-pulse">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500" />
                    </span>
                  )}
                  <Badge className={`bg-emerald-100 text-emerald-800 ${newNotifPulse ? 'ring-2 ring-red-400 animate-bounce' : 'animate-pulse'}`} data-testid="tech-notif-badge">
                    <Bell className="h-4 w-4 mr-1" />
                    {corrections.length} correction(s)
                  </Badge>
                </div>
              )}
              {/* Theme Toggle */}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={toggleDarkMode}
                title={darkMode ? 'Mode clair' : 'Mode sombre'}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button className="bg-[#8B7355] hover:bg-[#7A6548] text-white" onClick={() => nav('/intervention/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle intervention
              </Button>
            </div>
          </div>

          {/* Notifications: rapports corrigés & nouvelles missions */}
          {corrections.length > 0 && (
            <Card className="mb-8 border-emerald-300 bg-emerald-50" data-testid="tech-corrections-card">
              <CardContent className="p-6">
                <h3 className="font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notifications ({corrections.length})
                </h3>
                <div className="space-y-3">
                  {corrections.map(n => {
                    const isMission = n.type === 'new_mission';
                    return (
                    <div key={n.id} className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-emerald-200" data-testid="tech-correction-item">
                      <div className="flex items-center gap-3">
                        {isMission ? <Calendar className="h-5 w-5 text-[#8B7355] flex-shrink-0" /> : <Edit3 className="h-5 w-5 text-emerald-600 flex-shrink-0" />}
                        <div>
                          <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                            {n.message}
                            {isMission && n.has_documents && <Badge className="bg-red-500 text-white text-[10px]">📎 PDF à signer</Badge>}
                            {!isMission && <Badge className="bg-emerald-600 text-white text-[10px]">Relu</Badge>}
                          </div>
                          <p className="text-xs text-[#6B7280]">{formatDate(n.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {isMission && n.mission_id && (
                          <Button size="sm" className="bg-[#8B7355] hover:bg-[#7A6548] text-white" onClick={() => nav(`/intervention/new/${n.mission_id}`)} data-testid="tech-mission-start-btn">
                            Commencer
                          </Button>
                        )}
                        {!isMission && n.intervention_id && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => nav(`/intervention/${n.intervention_id}`)} data-testid="tech-correction-view-btn">
                            Voir
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => dismissCorrection(n.id)} data-testid="tech-correction-dismiss-btn">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Drafts section - Brouillons en cours */}
          {(drafts.length > 0 || localDraft) && (
            <Card id="drafts-section" className={`mb-8 ${oldDraftsCount > 0 ? 'border-red-300 bg-red-50' : 'border-orange-200 bg-orange-50'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold flex items-center gap-2 ${oldDraftsCount > 0 ? 'text-red-800' : 'text-orange-800'}`}>
                    <Edit3 className="h-5 w-5" />
                    Brouillons en cours ({drafts.length + (localDraft ? 1 : 0)})
                    {oldDraftsCount > 0 && (
                      <Badge className="bg-red-500 text-white animate-pulse ml-2">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {oldDraftsCount} ancien{oldDraftsCount > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </h3>
                  {oldDraftsCount > 0 && (
                    <span className="text-xs text-red-600 flex items-center gap-1">
                      <Bell className="h-3 w-3" />
                      N'oubliez pas de finaliser !
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  {/* Brouillon local */}
                  {localDraft && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border-2 border-orange-300">
                      <div className="flex items-center gap-3">
                        <CloudOff className="h-5 w-5 text-orange-500" />
                        <div>
                          <p className="font-medium">{localDraft.adresse_complete || 'Brouillon local'}</p>
                          <p className="text-sm text-[#6B7280]">
                            Étape {localDraft.current_step || 1}/12 • Sauvé localement
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={() => nav('/intervention/new')}>
                          Reprendre
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={deleteLocalDraft}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Brouillons serveur */}
                  {drafts.map(d => {
                    const isOld = getDraftAge(d.updated_at) > 24;
                    const isVeryOld = getDraftAge(d.updated_at) > 72; // > 3 jours
                    
                    return (
                      <div 
                        key={d.id} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                          isVeryOld 
                            ? 'bg-red-100 border-2 border-red-400 shadow-md' 
                            : isOld 
                              ? 'bg-amber-100 border-2 border-amber-400' 
                              : 'bg-white border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isVeryOld ? (
                            <AlertTriangle className="h-5 w-5 text-red-600 animate-pulse" />
                          ) : isOld ? (
                            <Clock className="h-5 w-5 text-amber-600" />
                          ) : (
                            <Cloud className="h-5 w-5 text-[#8B7355]" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{d.adresse_complete || 'Intervention en cours'}</p>
                              {isVeryOld && (
                                <Badge className="bg-red-500 text-white text-xs">Urgent</Badge>
                              )}
                              {isOld && !isVeryOld && (
                                <Badge className="bg-amber-500 text-white text-xs">En attente</Badge>
                              )}
                            </div>
                            <p className={`text-sm ${isOld ? 'text-amber-700' : 'text-[#6B7280]'}`}>
                              Étape {d.current_step || 1}/12 • {formatRelativeTime(d.updated_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className={`text-white ${
                              isVeryOld 
                                ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                                : isOld 
                                  ? 'bg-amber-600 hover:bg-amber-700' 
                                  : 'bg-[#8B7355] hover:bg-[#7A6548]'
                            }`} 
                            onClick={() => nav(`/intervention/new?draft=${d.id}`)}
                          >
                            {isVeryOld ? 'Finaliser !' : 'Reprendre'}
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => deleteDraft(d.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ALERTE FRANCHISES À RÉCUPÉRER */}
          {pendingFranchises.length > 0 && (
            <Card className="mb-8 border-green-300 bg-green-50" data-testid="franchise-alert">
              <CardContent className="p-6">
                <h3 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                  <Euro className="h-5 w-5" />
                  Franchises à récupérer ({pendingFranchises.length})
                  <Badge className="bg-green-600 text-white animate-pulse">Important</Badge>
                </h3>
                <div className="space-y-3">
                  {pendingFranchises.slice(0, 5).map(f => (
                    <div key={f.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="font-medium">{f.adresse_complete || 'Adresse non renseignée'}</p>
                          <p className="text-sm text-[#6B7280]">
                            {f.client_nom || 'Client'} • Montant: <span className="font-semibold text-green-700">{f.franchise_montant || 'Non précisé'}</span>
                          </p>
                          <p className="text-xs text-[#9CA3AF]">
                            Intervention du {new Date(f.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-green-500 text-green-700 hover:bg-green-100"
                        onClick={() => nav(`/intervention/${f.id}`)}
                      >
                        Voir détails
                      </Button>
                    </div>
                  ))}
                  {pendingFranchises.length > 5 && (
                    <p className="text-sm text-green-700 text-center">
                      +{pendingFranchises.length - 5} autre(s) franchise(s)
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming missions */}
          {missions.length > 0 && (
            <Card className="mb-8 border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Missions assignées ({missions.length})
                </h3>
                <div className="space-y-3">
                  {missions.slice(0, 3).map(m => (
                    <div key={m.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div>
                        <p className="font-medium">{m.title}</p>
                        <p className="text-sm text-[#6B7280]">{m.scheduled_date} {m.scheduled_time && `à ${m.scheduled_time}`}</p>
                        <p className="text-sm text-[#6B7280]">{m.address}</p>
                      </div>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => nav(`/intervention/new/${m.id}`)}>
                        Démarrer
                      </Button>
                    </div>
                  ))}
                </div>
                <Button variant="link" className="mt-2 text-blue-600" onClick={() => nav('/planning')}>
                  Voir tout le planning →
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <Card className="stat-card">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-[#F3F4F6]">
                    <FileText className="h-6 w-6 text-[#8B7355]" />
                  </div>
                  <div>
                    <p className="stat-value">{stats.total_interventions}</p>
                    <p className="stat-label">Total</p>
                  </div>
                </div>
              </Card>
              <Card className="stat-card">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-amber-50">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="stat-value">{stats.pending_review}</p>
                    <p className="stat-label">En attente</p>
                  </div>
                </div>
              </Card>
              <Card className="stat-card">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-green-50">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="stat-value">{stats.validated}</p>
                    <p className="stat-label">Validées</p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
              <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
          </div>

          {/* Interventions list */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : filteredInterventions.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <FileText className="h-12 w-12 text-[#D4C5A9] mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Aucune intervention</h3>
                <p className="text-[#6B7280] mb-4">{searchTerm ? 'Aucun résultat' : 'Créez votre première intervention'}</p>
                {!searchTerm && (
                  <Button className="bg-[#8B7355] hover:bg-[#7A6548] text-white" onClick={() => nav('/intervention/new')}>
                    <Plus className="h-4 w-4 mr-2" />Nouvelle intervention
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredInterventions.map((intervention) => (
                <Card key={intervention.id} className="card-hover cursor-pointer" onClick={() => nav(`/intervention/${intervention.id}`)}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-2">
                          {getUrgencyDot(intervention.urgency_color)}
                          <MapPin className="h-5 w-5 text-[#8B7355] flex-shrink-0" />
                          <div>
                            <h3 className="font-semibold">{intervention.adresse_complete}</h3>
                            <p className="text-sm text-[#6B7280]">{intervention.type_batiment} • {intervention.type_intervention?.join(', ')}</p>
                          </div>
                        </div>
                        {intervention.client_nom && <p className="text-sm text-[#6B7280] ml-8">Client: {intervention.client_nom}</p>}
                      </div>
                      <div className="flex items-center gap-4 ml-8 md:ml-0">
                        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                          <Calendar className="h-4 w-4" />
                          {formatDate(intervention.created_at)}
                        </div>
                        {getStatusBadge(intervention.status)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default TechnicianDashboard;
